/**
 * The Case IDE's view of the agent thread API: every call takes the
 * `individualId` and forwards to `AgentAPI` with `{ kind: "case", id }`.
 */
import type { UIMessage } from "ai";

import {
  AgentAPI,
  agentChatUrl,
  type AgentThread,
  type PlanUpdate
} from "@/features/agents/api/agentApi";
import type { AgentScope } from "@/features/agents/types";

import type { AgentMode, CaseAgentPlan } from "../../types";

export type {
  ActiveTurn,
  PendingQuestion,
  PlanUpdate
} from "@/features/agents/api/agentApi";
export { TurnInProgressError } from "@/features/agents/api/agentApi";

/** A thread as the Case IDE sees it (the scope is always a case). */
export type CaseAgentThread = AgentThread;

export const caseScope = (individualId: string): AgentScope => ({
  kind: "case",
  id: individualId
});

/** Absolute URL the AI SDK transport posts each turn to. */
export const caseAgentChatUrl = (
  individualId: string,
  threadId: string
): string => agentChatUrl(caseScope(individualId), threadId);

export const CaseAgentAPI = {
  listThreads: (individualId: string): Promise<CaseAgentThread[]> =>
    AgentAPI.listThreads(caseScope(individualId)),
  createThread: (
    individualId: string,
    title?: string
  ): Promise<CaseAgentThread> =>
    AgentAPI.createThread(caseScope(individualId), title),
  getThread: (
    individualId: string,
    threadId: string
  ): Promise<{ thread: CaseAgentThread; messages: UIMessage[] }> =>
    AgentAPI.getThread(caseScope(individualId), threadId),
  deleteThread: (individualId: string, threadId: string): Promise<void> =>
    AgentAPI.deleteThread(caseScope(individualId), threadId),
  postTeamMessage: (
    individualId: string,
    threadId: string,
    text: string,
    id?: string
  ): Promise<{ message: UIMessage; thread: CaseAgentThread }> =>
    AgentAPI.postTeamMessage(caseScope(individualId), threadId, text, id),
  updateThreadMode: (
    individualId: string,
    threadId: string,
    mode: AgentMode
  ): Promise<CaseAgentThread> =>
    AgentAPI.updateThreadMode(caseScope(individualId), threadId, mode),
  getPlan: (individualId: string, planId: string): Promise<CaseAgentPlan> =>
    AgentAPI.getPlan(caseScope(individualId), planId),
  updatePlan: (
    individualId: string,
    planId: string,
    updates: PlanUpdate
  ): Promise<CaseAgentPlan> =>
    AgentAPI.updatePlan(caseScope(individualId), planId, updates),
  buildPlan: (
    individualId: string,
    planId: string
  ): Promise<{
    thread: CaseAgentThread;
    plan: CaseAgentPlan;
    prompt: string;
  }> => AgentAPI.buildPlan(caseScope(individualId), planId),
  listWorkspacePlans: (individualId: string): Promise<CaseAgentPlan[]> =>
    AgentAPI.listWorkspacePlans(caseScope(individualId)),
  savePlanToWorkspace: (
    individualId: string,
    planId: string
  ): Promise<CaseAgentPlan> =>
    AgentAPI.savePlanToWorkspace(caseScope(individualId), planId)
};
